import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { doc, onSnapshot, serverTimestamp, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import type { RequirementCategory } from '../constants/categories';

// localStorage keys
const STORAGE_KEY = 'crevvi_user_preferences';

// Helper to load from localStorage
const loadFromStorage = (): UserPreferences | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore localStorage errors
  }
  return null;
};

// Helper to save to localStorage
const saveToStorage = (prefs: UserPreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore localStorage errors
  }
};

export interface UserPreferences {
  categories: RequirementCategory[];
  notificationsEnabled: boolean;
  updatedAt?: Timestamp | Date;
}

interface UserPreferencesContextValue {
  preferences: UserPreferences;
  loading: boolean;
  error: string | null;
  setCategories: (categories: RequirementCategory[]) => Promise<void>;
  toggleNotifications: (enabled: boolean) => Promise<void>;
  addCategory: (category: RequirementCategory) => Promise<void>;
  removeCategory: (category: RequirementCategory) => Promise<void>;
  isCategorySelected: (category: RequirementCategory) => boolean;
}

const defaultPreferences: UserPreferences = {
  categories: [],
  notificationsEnabled: false,
};

const UserPreferencesContext = createContext<UserPreferencesContextValue | undefined>(undefined);

// Debounce helper for Firestore writes
function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );
}

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to user preferences from Firestore (user document) and fallback to localStorage
  useEffect(() => {
    if (!user) {
      // Load from localStorage when not authenticated
      const stored = loadFromStorage();
      if (stored) {
        setPreferences(stored);
      } else {
        setPreferences(defaultPreferences);
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // First, try to load from localStorage for immediate hydration
    const stored = loadFromStorage();
    if (stored) {
      setPreferences(stored);
    }

    const userRef = doc(db, 'users', user.uid);
    
    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          // Check if preferences field exists
          if (data?.preferences) {
            const prefs: UserPreferences = {
              categories: data.preferences.categories || [],
              notificationsEnabled: data.preferences.notificationsEnabled || false,
              updatedAt: data.preferencesUpdatedAt || data.preferences.updatedAt,
            };
            setPreferences(prefs);
            // Sync to localStorage
            saveToStorage(prefs);
          } else if (!stored) {
            // No preferences in Firestore or localStorage, use defaults
            setPreferences(defaultPreferences);
          }
        } else {
          // User document doesn't exist, use localStorage or defaults
          if (!stored) {
            setPreferences(defaultPreferences);
          }
        }
        setLoading(false);
      },
      (err: any) => {
        console.error('Error loading user preferences:', err);
        // On permission error or other issues, use localStorage
        if (stored) {
          setPreferences(stored);
        } else {
          setPreferences(defaultPreferences);
        }
        setError('Failed to load preferences from server');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Persist to Firestore or localStorage with debounce
  const persistPreferences = useCallback(
    async (updates: Partial<UserPreferences>) => {
      if (!user) return;

      // Always save to localStorage as backup
      const current = loadFromStorage() || defaultPreferences;
      const merged = { ...current, ...updates, updatedAt: new Date() };
      saveToStorage(merged);

      // Try to save to Firestore (user document, not subcollection to avoid permission issues)
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          preferences: {
            categories: merged.categories,
            notificationsEnabled: merged.notificationsEnabled,
          },
          preferencesUpdatedAt: serverTimestamp(),
        });
      } catch (err: any) {
        // If permission denied or other error, silently use localStorage only
        if (err?.code === 'permission-denied') {
          console.warn('Firestore permissions denied, using localStorage for preferences');
        } else {
          console.error('Error saving preferences to Firestore:', err);
        }
        // Preferences are already saved to localStorage above
      }
    },
    [user]
  );

  const debouncedPersist = useDebouncedCallback(persistPreferences, 500);

  // Set categories (replace all)
  const setCategories = useCallback(
    async (categories: RequirementCategory[]) => {
      if (!user) {
        showToast('Please sign in to manage interests', 'info');
        return;
      }

      // Optimistic update
      setPreferences((prev) => ({ ...prev, categories }));
      
      // Debounced persist
      debouncedPersist({ categories });
    },
    [user, debouncedPersist, showToast]
  );

  // Toggle notifications
  const toggleNotifications = useCallback(
    async (enabled: boolean) => {
      if (!user) {
        showToast('Please sign in to manage notifications', 'info');
        return;
      }

      // Optimistic update
      setPreferences((prev) => ({ ...prev, notificationsEnabled: enabled }));
      
      // Debounced persist
      debouncedPersist({ notificationsEnabled: enabled });

      // Request browser notification permission if enabling
      if (enabled && 'Notification' in window) {
        const currentPermission = Notification.permission;
        
        if (currentPermission === 'denied') {
          // Notifications are blocked - show guidance on how to reset
          showToast(
            'Notifications blocked. Click the 🔒 icon in the address bar to enable.',
            'info',
            { duration: 6000 }
          );
        } else {
          const permission = await Notification.requestPermission();
          if (permission === 'denied') {
            showToast(
              'Notifications blocked. You can enable them later via the site settings.',
              'info',
              { duration: 5000 }
            );
          } else if (permission === 'granted') {
            showToast('Notifications enabled! You will receive alerts for matching requirements.', 'success');
          }
        }
      }
    },
    [user, debouncedPersist, showToast]
  );

  // Add a single category
  const addCategory = useCallback(
    async (category: RequirementCategory) => {
      if (!user) {
        showToast('Please sign in to add interests', 'info');
        return;
      }

      setPreferences((prev) => {
        if (prev.categories.includes(category)) return prev;
        const newCategories = [...prev.categories, category];
        debouncedPersist({ categories: newCategories });
        return { ...prev, categories: newCategories };
      });
    },
    [user, debouncedPersist, showToast]
  );

  // Remove a single category
  const removeCategory = useCallback(
    async (category: RequirementCategory) => {
      if (!user) {
        showToast('Please sign in to remove interests', 'info');
        return;
      }

      setPreferences((prev) => {
        const newCategories = prev.categories.filter((c) => c !== category);
        debouncedPersist({ categories: newCategories });
        return { ...prev, categories: newCategories };
      });
    },
    [user, debouncedPersist, showToast]
  );

  // Check if category is selected
  const isCategorySelected = useCallback(
    (category: RequirementCategory) => {
      return preferences.categories.includes(category);
    },
    [preferences.categories]
  );

  const value = useMemo(
    () => ({
      preferences,
      loading,
      error,
      setCategories,
      toggleNotifications,
      addCategory,
      removeCategory,
      isCategorySelected,
    }),
    [preferences, loading, error, setCategories, toggleNotifications, addCategory, removeCategory, isCategorySelected]
  );

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}
